import React from "react";

import { useTranslation } from "react-i18next";

import {
  PaymentMethodForm,
  useSetupIntentReturn,
} from "@/components/billing/payment-method-form";
import { PaymentMethodSummary } from "@/components/billing/payment-method-summary";
import Button from "@/components/button";
import { RoleLink } from "@/components/role-link";
import {
  useBillingPortalMutation,
  useBillingStatusQuery,
  useResumeBillingMutation,
} from "@/queries/hooks/billingHooks";
import type { BillingStatus, BillingTier } from "@/schemas/billing";
import { notify } from "@/utils";
import {
  formatBillingDate,
  formatCents,
  isSubscriptionEnded,
  planScheduleLine,
  tierLabel,
} from "@/utils/billing";

/** Settings → Billing (employers; flag on AND billing/status.enabled). */
export function BillingPanel() {
  const { t } = useTranslation("billing");
  const statusQuery = useBillingStatusQuery();
  const portal = useBillingPortalMutation();
  const [updating, setUpdating] = React.useState(false);
  const status = statusQuery.data;

  const returnUrl = `${window.location.origin}${window.location.pathname}?tab=billing`;

  const onSaved = React.useCallback(() => {
    setUpdating(false);
    notify.success(t("form.saved"));
  }, [t]);
  const { pending: returnPending } = useSetupIntentReturn({
    onConfirmed: onSaved,
  });

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      <div>
        <h3 className="text-xl font-semibold text-garnet mb-2">
          {t("settings.title")}
        </h3>
        <p className="text-sm text-garnet/80">{t("settings.description")}</p>
      </div>

      {statusQuery.isLoading || returnPending ? (
        <p className="body-small text-gray-500">{t("form.loading")}</p>
      ) : !status ? (
        <div className="flex flex-col gap-3 items-start">
          <p className="body-small text-red-600" role="alert">
            {t("settings.loadFailed")}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => statusQuery.refetch()}
          >
            {t("form.tryAgain")}
          </Button>
        </div>
      ) : !status.enabled ? (
        <p className="body-small text-garnet/80">{t("settings.notEnabled")}</p>
      ) : (
        <>
          <PlanCard status={status} />

          <div className="flex flex-col gap-3">
            <h4 className="font-semibold text-garnet">
              {t("settings.paymentMethodTitle")}
            </h4>
            {updating ? (
              <div className="max-w-[520px]">
                <PaymentMethodForm
                  returnUrl={returnUrl}
                  submitLabel={t("form.save")}
                  onSaved={onSaved}
                  onCancel={() => setUpdating(false)}
                />
              </div>
            ) : (
              <>
                {status.paymentMethod ? (
                  <PaymentMethodSummary
                    paymentMethod={status.paymentMethod}
                    pending={status.paymentMethodPending}
                  />
                ) : status.paymentMethodPending ? (
                  <p className="body-small text-gray-600">
                    {t("paymentMethod.pendingVerification")}
                  </p>
                ) : (
                  <p className="body-small text-gray-600">
                    {t("settings.noPaymentMethod")}
                  </p>
                )}
                <div className="flex flex-row flex-wrap gap-3">
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => setUpdating(true)}
                  >
                    {t("resolve.updatePaymentMethod")}
                  </Button>
                  {/* The portal needs a Stripe customer with a payment method. */}
                  {status.paymentMethod && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => portal.mutate(returnUrl)}
                      disabled={portal.isPending}
                    >
                      {t("manageBilling")}
                    </Button>
                  )}
                </div>
                {status.paymentMethod && (
                  <p className="text-xs text-garnet/70">
                    {t("settings.portalHint")}
                  </p>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function useTierText() {
  const { t } = useTranslation("billing");
  return React.useCallback(
    (tier: Pick<BillingTier, "minWorkers" | "maxWorkers">) => {
      const label = tierLabel(tier);
      return t(label.key, label.params);
    },
    [t],
  );
}

function PlanCard({ status }: { status: BillingStatus }) {
  const { t, i18n } = useTranslation("billing");
  const tierText = useTierText();
  const statusQuery = useBillingStatusQuery();
  const resume = useResumeBillingMutation();
  const price =
    status.priceAmountCents !== null
      ? formatCents(status.priceAmountCents, status.currency, i18n.language)
      : null;
  const pendingPrice =
    status.pendingPriceAmountCents !== null
      ? formatCents(
          status.pendingPriceAmountCents,
          status.currency,
          i18n.language,
        )
      : null;
  const trialEnd = formatBillingDate(status.trialEnd);
  const periodEnd = formatBillingDate(status.currentPeriodEnd);
  const changeDate = periodEnd ?? trialEnd;
  const problem = status.actionRequired || status.blocked;

  const line = planScheduleLine(status);
  let scheduleLine: string | null = null;
  if (line) {
    scheduleLine =
      "date" in line
        ? t(line.key, { date: formatBillingDate(line.date) ?? line.date })
        : "count" in line
          ? t(line.key, { count: line.count })
          : t(line.key);
  }
  const showResume =
    status.cancelAtPeriodEnd && !isSubscriptionEnded(status.status);

  async function handleResume() {
    try {
      await resume.mutateAsync();
      notify.success(t("settings.resumed"));
      void statusQuery.refetch();
    } catch (e) {
      notify.error(
        e instanceof Error && e.message ? e.message : t("settings.resumeFailed"),
      );
    }
  }

  return (
    <div className="rounded-lg ring-1 ring-garnet/20 bg-fog/40 p-4 flex flex-col gap-2">
      <div className="body-small text-gray-500">{t("settings.planLabel")}</div>
      <div className="flex flex-row flex-wrap items-baseline gap-x-3">
        <span className="text-lg font-semibold text-garnet">
          {status.tier ? tierText(status.tier) : t("settings.noPlan")}
        </span>
        {price ? (
          <span className="body-small text-garnet/80">
            {t("settings.pricePerMonth", { price })}
          </span>
        ) : null}
      </div>
      <p className="body-small text-garnet/80">
        {t("settings.rosterSize", { count: status.rosterSize })}
      </p>
      {scheduleLine ? (
        <p className="body-small text-garnet/80">{scheduleLine}</p>
      ) : null}
      {showResume ? (
        <div className="pt-1">
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleResume}
            disabled={resume.isPending}
          >
            {resume.isPending ? t("form.saving") : t("resume.button")}
          </Button>
        </div>
      ) : null}
      {status.pendingTier && changeDate && !status.cancelAtPeriodEnd ? (
        <p className="body-small font-semibold text-garnet">
          {pendingPrice
            ? t("settings.pendingTierWithPrice", {
                tier: tierText(status.pendingTier),
                date: changeDate,
                price: pendingPrice,
              })
            : t("settings.pendingTier", {
                tier: tierText(status.pendingTier),
                date: changeDate,
              })}
        </p>
      ) : null}
      {problem ? (
        <div className="body-small text-red-700 flex flex-row flex-wrap gap-2 items-center">
          <span>{t("settings.paymentProblem")}</span>
          <RoleLink to="/app/$role/billing/resolve" className="body-small">
            {t("settings.resolveLink")}
          </RoleLink>
        </div>
      ) : null}
    </div>
  );
}
